// Parsers e helpers de manifest M3U8 reutilizados pelos downloaders HLS.
// Mantido livre de dependências de runtime para poder rodar em qualquer ambiente.

function resolveUrl (relative, base) {
  try {
    return new URL(relative, base).href;
  } catch {
    return relative;
  }
}

// Parseia o master manifest e retorna **todos** os variants ordenados por
// bandwidth crescente, mais o variant escolhido conforme `preferQuality`.
// Retorna null se o texto não for um master manifest (sem #EXT-X-STREAM-INF —
// pode ser media manifest direto, comum em vídeos com 1 só variant).
//
// `preferQuality` aceita 'low' | 'medium' | 'high' (default 'low' por
// retrocompat com chamadas antigas que esperavam menor bitrate). Buckets:
//   - low:    pega o MAIOR variant com resolução ≤ 480p (ou o menor se não houver)
//   - medium: pega o MAIOR variant na faixa 481-720p (ou mais próximo)
//   - high:   pega o MAIOR variant disponível (sem teto)
//
// Resolution-based em vez de bandwidth-based porque BANDWIDTH em HLS é
// suspeito (varia muito por encoder/profile). RESOLUTION é mais estável e
// semanticamente alinhado com a percepção do user ("eu quero Alta = 1080p").
export function parseMasterManifest (text, baseUrl, options = {}) {
  const preferQuality = options.preferQuality || 'low';
  const lines = text.split('\n');
  const variants = [];
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();
    if (line.startsWith('#EXT-X-STREAM-INF')) {
      const bandwidthMatch = line.match(/BANDWIDTH=(\d+)/);
      const bandwidth = bandwidthMatch ? parseInt(bandwidthMatch[1]) : 0;
      const resMatch = line.match(/RESOLUTION=(\d+)x(\d+)/);
      const height = resMatch ? parseInt(resMatch[2]) : 0;

      // A URI do variant é a próxima linha ÚTIL, não necessariamente `i + 1`:
      // a RFC 8216 manda ignorar linhas em branco, e comentários (`#` que não
      // seja tag) podem aparecer no meio. Olhar só `i + 1` fazia um master
      // perfeitamente válido sair sem variant nenhum → parse devolvia null →
      // o chamador reprocessava o MASTER como media playlist e as .m3u8 dos
      // variants viravam "segmentos", com o download reportando sucesso.
      let uri = null;
      for (let j = i + 1; j < lines.length; j++) {
        const next = lines[j].trim();
        if (!next) continue;
        // Outra tag antes da URI: este STREAM-INF está órfão, desiste dele.
        if (next.startsWith('#EXT')) break;
        if (next.startsWith('#')) continue;
        uri = next;
        break;
      }
      if (uri) {
        variants.push({ bandwidth, height, url: resolveUrl(uri, baseUrl) });
      }
    }
  }
  if (!variants.length) return null;
  variants.sort((a, b) => a.bandwidth - b.bandwidth);
  return selectVariantForQuality(variants, preferQuality);
}

// Mapeia uma qualidade lógica ('low'/'medium'/'high') pra um variant concreto.
// Estratégia "best in bucket, fallback nearest":
//   1. Tenta achar variants dentro do range de resolução do bucket
//   2. Se achar, retorna o de MAIOR resolução dentro do bucket (max qualidade lá)
//   3. Se bucket vazio, fallback pro variant mais próximo do range (clamp)
//
// `variants` precisa vir ORDENADO por bandwidth ascendente (parseMasterManifest
// faz isso). Os variants podem ter `height = 0` se #EXT-X-STREAM-INF veio sem
// RESOLUTION — nesses casos cai num path mais simples por bandwidth.
export function selectVariantForQuality (variants, quality) {
  if (!variants || !variants.length) return null;

  // Fallback: variants sem height (RESOLUTION ausente no manifest) — usa
  // bandwidth como proxy. low = menor; high = maior; medium = mediano.
  const hasHeights = variants.some(v => v.height > 0);
  if (!hasHeights) {
    if (quality === 'high') return variants[variants.length - 1];
    if (quality === 'medium') return variants[Math.floor(variants.length / 2)];
    return variants[0];
  }

  // Buckets por height (px do lado vertical da resolução).
  // Pega o de MAIOR resolução dentro do bucket — "máximo dentro da
  // categoria escolhida". Bucket vazio → cai pro mais próximo (clamp).
  let candidates;
  if (quality === 'low') {
    candidates = variants.filter(v => v.height > 0 && v.height <= 480);
    if (!candidates.length) candidates = [variants[0]];
  } else if (quality === 'medium') {
    candidates = variants.filter(v => v.height > 480 && v.height <= 720);
    if (!candidates.length) {
      // Sem variant no range — escolhe o mais próximo do meio (720)
      const sortedByDistance = variants
        .filter(v => v.height > 0)
        .map(v => ({ v, dist: Math.abs(v.height - 600) }))
        .sort((a, b) => a.dist - b.dist);
      candidates = [sortedByDistance[0].v];
    }
  } else { // 'high'
    candidates = variants.filter(v => v.height > 720);
    if (!candidates.length) {
      // Sem 1080p+ → pega o maior disponível
      candidates = [variants[variants.length - 1]];
    }
  }
  // Dentro do bucket, pega o de maior height (e em empate, maior bandwidth)
  candidates.sort((a, b) => b.height - a.height || b.bandwidth - a.bandwidth);
  return candidates[0];
}

// Retorna `isMaster: true` quando o texto é, na verdade, um master playlist —
// o chamador precisa saber disso pra falhar em vez de seguir com lixo.
export function parseMediaManifest (text, baseUrl) {
  const lines = text.split('\n');
  const segments = [];
  let keyUrl = null;
  let totalDuration = 0;
  let pendingDuration = 0;
  let pendingSegment = false;
  let hasFmp4Init = false;
  let hasStreamInf = false;
  let sawExtinf = false;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();

    if (line.startsWith('#EXT-X-STREAM-INF')) hasStreamInf = true;

    if (line.startsWith('#EXT-X-KEY')) {
      const uriMatch = line.match(/URI="([^"]+)"/);
      if (uriMatch) {
        keyUrl = resolveUrl(uriMatch[1], baseUrl);
      }
    }

    // #EXT-X-MAP:URI="init.mp4" — init segment para fmp4. NÃO tratado por
    // este parser (Wowza atual entrega MPEG-TS sem MAP). Se aparecer, o LO
    // playback offline vai falhar porque o init segment não foi baixado nem
    // reescrito no manifest local. Logamos pra detectar quando Wowza/Akamai
    // mudarem o pipeline pra fmp4 — TODO suportar quando virar problema.
    if (line.startsWith('#EXT-X-MAP')) {
      hasFmp4Init = true;
    }

    // #EXTINF:<duration>,<title?> — sempre precede o URI do segmento (mesmo que
    // existam tags entre eles, ex: #EXT-X-DISCONTINUITY). Acumulamos o último
    // valor visto e zeramos ao consumir, pra somar a duração total do vídeo.
    if (line.startsWith('#EXTINF:')) {
      const durMatch = line.match(/#EXTINF:([\d.]+)/);
      if (durMatch) pendingDuration = parseFloat(durMatch[1]) || 0;
      // Flag separado do valor: `#EXTINF:0` é duração legítima e não pode
      // fazer o segmento seguinte ser descartado.
      pendingSegment = true;
      sawExtinf = true;
    }

    // Só conta como segmento URI precedida de #EXTINF, que é o que a RFC 8216
    // exige de toda Media Segment. Antes aqui bastava "linha que não é
    // comentário", e qualquer texto virava lista de segmentos: as .m3u8 de um
    // master reprocessado, ou o HTML de um portal cativo que respondeu 200.
    // O download ia até o fim e reportava sucesso sobre lixo.
    if (line && !line.startsWith('#') && pendingSegment) {
      const absUrl = resolveUrl(line, baseUrl);
      // Filename derivado do ÍNDICE (não do pathname) para garantir unicidade.
      // Wowza pode entregar URLs com mesmo path mas query diferente (ex: tokens
      // assinados rotativos): https://cdn/foo.ts?token=A e https://cdn/foo.ts?token=B
      // produziam o mesmo filename antes → segmento sobrescrevia o outro → glitch
      // de playback. Extensão extraída da URL pra preservar tipo (.ts, .m4s, .aac).
      let ext = 'ts';
      try {
        const m = new URL(absUrl).pathname.match(/\.([a-z0-9]+)$/i);
        if (m) ext = m[1];
      } catch { /* mantém .ts */ }
      const filename = `seg_${segments.length}.${ext}`;
      segments.push({ url: absUrl, filename, originalLine: line });
      totalDuration += pendingDuration;
      pendingDuration = 0;
      pendingSegment = false;
    }
  }

  if (hasFmp4Init) {
    console.warn('[hlsManifestParser] #EXT-X-MAP detectado — manifest aparenta ser fmp4. Offline playback pode falhar (init segment não baixado).');
  }

  return { keyUrl, segments, totalDuration, hasFmp4Init, isMaster: hasStreamInf, sawExtinf };
}

// Reescreve o manifest pra apontar segmentos pros paths locais e (opcionalmente)
// injetar/reescrever a tag #EXT-X-KEY apontando pra uma URI local (usada com o
// scheme handler `vxlkey://` no nativo).
//
// `options.localKeyUri` é a URI que o VHS vai fetchar pra obter os 16 bytes da
// chave AES-128. No nosso caso é "vxlkey://<loId>", resolvido pelo
// VxlKeySchemeHandler (iOS) / VxlKeyWebViewClient (Android) que descifra a
// File Key do hardware-backed KeyVault.
//
// Quando `localKeyUri` está presente, FORÇA a tag #EXT-X-KEY no manifest:
//   - Se já existe(m) tag(s), reescreve TODAS pra apontar pra localKeyUri
//   - Se não existe, INJETA uma logo após #EXTM3U
// Cobre tanto o caso "Wowza sem cupertinoEncryption" (atual: zero tags) quanto
// o caso futuro "Wowza com cifragem nativa" (uma ou mais tags) — em ambos, o
// VHS resolve a chave via o scheme local.
export function rewriteManifest (text, segments, options = {}) {
  const { localKeyUri = null } = options;
  let result = text;

  segments.forEach(({ originalLine, filename }) => {
    // Cada URL de segmento aparece uma vez no manifest (garantia HLS), então
    // replace simples é suficiente. Mantido conservador.
    result = result.replace(originalLine, filename);
  });

  if (localKeyUri) {
    if (/#EXT-X-KEY/.test(result)) {
      // Caso "Wowza emite EXT-X-KEY": reescreve URI mantendo IV/KEYFORMAT/etc.
      result = result.replace(/#EXT-X-KEY:([^\n\r]*)/g, (_full, attrs) => {
        const updated = /URI="[^"]*"/.test(attrs)
          ? attrs.replace(/URI="[^"]*"/, `URI="${localKeyUri}"`)
          : `${attrs};URI="${localKeyUri}"`;
        return `#EXT-X-KEY:${updated}`;
      });
    } else {
      // Caso atual (Wowza não cifra): injeta a tag logo após #EXTM3U.
      // HLS spec aceita #EXT-X-KEY em qualquer ponto antes do primeiro segmento.
      // METHOD=AES-128 + sem IV explícito → VHS deriva IV do sequence number
      // (que cifragem do plugin nativo replica usando segmentIndex).
      result = result.replace(
        /(#EXTM3U[^\n]*\r?\n)/,
        `$1#EXT-X-KEY:METHOD=AES-128,URI="${localKeyUri}"\n`
      );
    }

    // Força MEDIA-SEQUENCE=0 no manifest local. Sem isso, se o Wowza
    // emitir um valor diferente (raro em VOD mas possível), o VHS calcularia
    // IV a partir de `sequence + idx` enquanto o plugin nativo cifra com
    // IV = idx (0-based puro) → mismatch → playback toca lixo.
    if (/#EXT-X-MEDIA-SEQUENCE/.test(result)) {
      result = result.replace(/#EXT-X-MEDIA-SEQUENCE:\d+/, '#EXT-X-MEDIA-SEQUENCE:0');
    } else {
      result = result.replace(
        /(#EXTM3U[^\n]*\r?\n)/,
        '$1#EXT-X-MEDIA-SEQUENCE:0\n'
      );
    }
  }

  return result;
}
